/**
 * Purchase Summary Report Filters Component
 * @see Sprint 32 - Rapport Sommaire des Achats
 * @see US-32.3 - Frontend Page et Composants
 */

import { useMemo } from 'react';
import { Select, DatePicker } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useActiveSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { GoodsReceiptStatus, GOODS_RECEIPT_STATUS_LABELS } from '@bstock/shared';

interface PurchaseSummaryFiltersProps {
  dateRange: DateRange;
  supplierId: string;
  status: string;
  onDateRangeChange: (range: DateRange) => void;
  onSupplierChange: (value: string) => void;
  onStatusChange: (value: string) => void;
}

export function PurchaseSummaryFilters({
  dateRange,
  supplierId,
  status,
  onDateRangeChange,
  onSupplierChange,
  onStatusChange,
}: PurchaseSummaryFiltersProps) {
  // Fetch active suppliers for filter
  const { data: suppliers = [] } = useActiveSuppliers();

  // Supplier options
  const supplierOptions = useMemo(
    () => [
      { value: '', label: 'Tous les fournisseurs' },
      ...suppliers.map((s: { id: string; name: string }) => ({
        value: s.id,
        label: s.name,
      })),
    ],
    [suppliers]
  );

  // Status options
  const statusOptions = useMemo(
    () => [
      { value: 'VALIDATED', label: GOODS_RECEIPT_STATUS_LABELS[GoodsReceiptStatus.VALIDATED] },
      { value: 'DRAFT', label: GOODS_RECEIPT_STATUS_LABELS[GoodsReceiptStatus.DRAFT] },
      { value: 'ALL', label: 'Tous les statuts' },
    ],
    []
  );

  return (
    <div className="flex flex-wrap items-end gap-4">
      {/* Date range picker */}
      <div className="w-80">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Periode
        </label>
        <DatePicker
          maxDate={new Date()}
          value={dateRange}
          onChange={onDateRangeChange}
          placeholder="Selectionner une periode"
        />
      </div>

      {/* Supplier filter */}
      <div className="w-56">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Fournisseur
        </label>
        <Select
          value={supplierId}
          onChange={onSupplierChange}
          options={supplierOptions}
          placeholder="Fournisseur"
          allowEmpty={false}
        />
      </div>

      {/* Status filter */}
      <div className="w-40">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Statut
        </label>
        <Select
          value={status}
          onChange={onStatusChange}
          options={statusOptions}
          placeholder="Statut"
          allowEmpty={false}
        />
      </div>
    </div>
  );
}
